import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

/**
 * Пикер user-родителя для формы контакта. Сценарий: оператор открывает
 * карточку ученика, заводит «Маму», и хочет указать, что это та же мама,
 * которая уже есть у другого ребёнка. Выбирает её из пикера — и у обоих
 * детей контакты ссылаются на одного и того же `users.id`.
 *
 * value:    userId (number | null)
 * onPick:   ({ id, name, email, skudPersonId, studentsCount }) — выбран
 * onClear:  () — отвязать
 * excludeStudentId: не используется внутри пикера, но прокидывается в
 *                   логику «уже привязан к этим ученикам», чтобы исключить
 *                   текущего из списка.
 *
 * После выбора показывается алерт «уже привязан к ученикам: A, B». Если
 * родитель ещё ни у кого не привязан — алерта не будет.
 */
export default function ParentUserPicker({ value, onPick, onClear, excludeStudentId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // Текущая привязка — подтянем список детей этого родителя для алерта.
  const [currentUser, setCurrentUser]       = useState(null);
  const [linkedStudents, setLinkedStudents] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (!value) {
      setCurrentUser(null);
      setLinkedStudents([]);
      return undefined;
    }
    let aborted = false;

    // Параллельно:
    //  - тащим самого user-родителя (ФИО, email) для отображения в кнопке;
    //  - тащим список его детей (для алерта «уже привязан также к…»).
    api.get(`/api/parents/${value}`)
      .then((res) => {
        if (aborted) return;
        const p = res.parent;
        if (p) setCurrentUser({ id: p.id, name: p.name, email: p.email });
      })
      .catch(() => {});

    api.get(`/api/parents/${value}/students`)
      .then((res) => {
        if (aborted) return;
        const list = (res.rows || []).filter((s) => s.id !== excludeStudentId);
        setLinkedStudents(list);
      })
      .catch(() => {});

    return () => { aborted = true; };
  }, [value, excludeStudentId]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      setLoading(true); setError(null);
      try {
        const qs = new URLSearchParams();
        if (search) qs.set('q', search);
        qs.set('limit', '30');
        const data = await api.get('/api/parents?' + qs.toString());
        setResults(data.rows || []);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [search, open]);

  return (
    <div className="ad-picker" ref={ref}>
      <div className="ad-picker__row">
        <button
          type="button"
          className="ad-picker__current"
          onClick={() => setOpen((o) => !o)}
        >
          {value ? (
            <span className="ad-picker__value">
              <span className="ad-picker__name">
                {currentUser ? currentUser.name : `Пользователь #${value}`}
              </span>
            </span>
          ) : (
            <span className="ad-picker__empty">Не выбран — создать новую запись</span>
          )}
          <Icon name="chevronDown" size={10} />
        </button>
        {value && (
          <button
            type="button"
            className="ad-picker__clear"
            onClick={() => { onClear?.(); setCurrentUser(null); setLinkedStudents([]); }}
            title="Отвязать"
          >
            <Icon name="x" size={12} />
          </button>
        )}
      </div>

      {/* Алерт «привязан ещё к таким-то детям» — после выбора. */}
      {value && linkedStudents.length > 0 && (
        <div style={{
          marginTop: 6,
          fontSize: 12.5,
          color: 'var(--fg-muted)',
          padding: '6px 10px',
          background: 'var(--panel-2, #f6f6f6)',
          border: '1px solid var(--border)',
          borderRadius: 6,
        }}>
          Этот же родитель привязан также к:&nbsp;
          {linkedStudents.map((s, i) => (
            <React.Fragment key={s.id}>
              {i > 0 && ', '}
              <b>{s.name}</b>
              {s.role && <span style={{ color: 'var(--fg-faint)' }}> ({s.role})</span>}
            </React.Fragment>
          ))}
        </div>
      )}

      {open && (
        <div className="ad-picker__menu">
          <div style={{ padding: 6 }}>
            <input
              className="ad-picker__search"
              placeholder="Поиск: ФИО или email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{ width: '100%' }}
            />
          </div>
          {error && <div className="ad-picker__error">{error}</div>}
          <div className="ad-picker__list">
            {loading && <div className="ad-picker__hint">Поиск…</div>}
            {!loading && !error && results.map((r) => (
              <button
                key={r.id}
                type="button"
                className="ad-picker__item"
                onClick={() => {
                  setCurrentUser(r);
                  onPick?.(r);
                  setOpen(false);
                }}
              >
                <span className="ad-picker__item-name">{r.name}</span>
                <span className="ad-picker__item-meta">
                  {r.email && <span style={{ marginRight: 8 }} className="mono">{r.email}</span>}
                  {r.studentsCount > 0 && (
                    <span title="К скольким ученикам уже привязан">
                      {r.studentsCount} реб.
                    </span>
                  )}
                </span>
              </button>
            ))}
            {!loading && !error && results.length === 0 && (
              <div className="ad-picker__hint">
                {search
                  ? 'Ничего не найдено среди родителей.'
                  : 'Начните вводить ФИО или email'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
